<template>
  <div class="vlm-configuration-list">
    <b-row class="pt-2 pb-1 align-items-center">
      <b-col cols="12" md="8">
        <h2 class="mb-25">{{ $t('vlm_configuration.title') }}</h2>
      </b-col>
      <b-col cols="12" md="4" class="d-flex justify-content-md-end mt-1 mt-md-0">
        <b-button v-if="canUpdate" variant="primary" @click="createConfiguration">
          <feather-icon icon="PlusIcon" class="mr-50" />
          {{ $t('vlm_configuration.add_configuration') }}
        </b-button>
      </b-col>
    </b-row>

    <b-alert v-if="!canUpdate" show variant="warning" class="mb-2">
      <div class="alert-body">{{ $t('vlm_configuration.read_only') }}</div>
    </b-alert>

    <b-card no-body>
      <div v-if="isLoading" class="text-center py-3">
        <b-spinner variant="primary" />
      </div>
      <b-table
        v-else
        :fields="fields"
        :items="tableRows"
        :empty-text="$t('vlm_configuration.empty_list')"
        responsive
        show-empty
      >
        <template #cell(enabled)="{ item }">
          <b-badge :variant="item.enabled ? 'light-success' : 'light-secondary'">
            {{ item.enabled ? $t('vlm_configuration.active') : $t('vlm_configuration.inactive') }}
          </b-badge>
        </template>
        <template #cell(updated_at)="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>
        <template #cell(actions)="{ item }">
          <b-button v-if="canUpdate" size="sm" variant="outline-primary" @click="editConfiguration(item.id)">
            <feather-icon icon="EditIcon" size="15" />
          </b-button>
          <b-button
            v-if="canDelete"
            size="sm"
            variant="outline-danger"
            class="ml-50"
            @click="configurationToDelete = item"
          >
            <feather-icon icon="Trash2Icon" size="15" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <b-modal
      v-model="showDeleteModal"
      :title="$t('vlm_configuration.delete_configuration')"
      :ok-title="$t('vlm_configuration.delete')"
      :cancel-title="$t('vlm_configuration.cancel')"
      ok-variant="danger"
      @ok="deleteConfiguration"
    >
      {{ $t('vlm_configuration.delete_confirmation', { name: configurationToDelete ? configurationToDelete.name : '' }) }}
    </b-modal>
  </div>
</template>

<script>
import { computed, onMounted, ref } from '@vue/composition-api'
import { BAlert, BBadge, BButton, BCard, BCol, BModal, BRow, BSpinner, BTable } from 'bootstrap-vue'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import ability from '@/libs/acl/ability'

export default {
  name: 'VlmConfigurationList',
  components: { BAlert, BBadge, BButton, BCard, BCol, BModal, BRow, BSpinner, BTable },
  setup(_, { root }) {
    const t = (key, params) => root.$t(key, params)
    const tc = (key, number) => root.$tc(key, number)
    const router = root.$router
    const toast = useToast()
    const configurations = ref([])
    const isLoading = ref(false)
    const configurationToDelete = ref(null)
    const canUpdate = computed(() => ability.can('update', 'AiAnalyticServer'))
    const canDelete = computed(() => ability.can('delete', 'AiAnalyticServer'))
    const showDeleteModal = computed({
      get: () => Boolean(configurationToDelete.value),
      set: value => {
        if (!value) configurationToDelete.value = null
      },
    })
    const fields = computed(() => [
      { key: 'name', label: t('vlm_configuration.name') },
      { key: 'camera_count', label: t('vlm_configuration.selected_cameras') },
      { key: 'model', label: t('vlm_configuration.model') },
      { key: 'enabled', label: t('vlm_configuration.service_status') },
      { key: 'updated_at', label: t('vlm_configuration.updated_at') },
      { key: 'actions', label: tc('actions.name', 2) },
    ])
    const tableRows = computed(() => configurations.value.map(configuration => ({
      ...configuration,
      camera_count: configuration.camera_proxy_ids.length,
    })))

    const notify = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon', variant },
      })
    }

    const loadConfigurations = async () => {
      isLoading.value = true
      try {
        const response = await axios.get('v1/vlm/configurations')
        configurations.value = response.data.data
      } catch (error) {
        notify(error?.response?.data?.message || t('vlm_configuration.load_error'), 'danger')
      } finally {
        isLoading.value = false
      }
    }

    const createConfiguration = () => router.push({ name: 'vlm_configuration_create' })
    const editConfiguration = id => router.push({ name: 'vlm_configuration_edit', params: { id } })
    const formatDate = value => (value ? new Date(value).toLocaleString() : t('vlm_configuration.not_saved'))

    const deleteConfiguration = async event => {
      if (!configurationToDelete.value || !canDelete.value) return

      event.preventDefault()
      try {
        await axios.delete(`v1/vlm/configurations/${configurationToDelete.value.id}`)
        configurations.value = configurations.value.filter(item => item.id !== configurationToDelete.value.id)
        configurationToDelete.value = null
        notify(t('vlm_configuration.deleted'), 'success')
      } catch (error) {
        notify(error?.response?.data?.message || t('vlm_configuration.delete_error'), 'danger')
      }
    }

    onMounted(loadConfigurations)

    return {
      canDelete,
      canUpdate,
      configurationToDelete,
      createConfiguration,
      deleteConfiguration,
      editConfiguration,
      fields,
      formatDate,
      isLoading,
      showDeleteModal,
      tableRows,
    }
  },
}
</script>

<style lang="scss" scoped>
.vlm-configuration-list {
  padding: 0 15px;
}
</style>
